import { Link } from "react-router-dom";
import {
  Tent, ShoppingBag, Heart, HandHeart, Gift, ArrowRight,
} from "lucide-react";

/**
 * FiveUserTypes — landing-page showcase of the AddVendTour community.
 *
 * Every visitor should see themselves in the first scroll. Five cards,
 * one per role, each with a role-specific CTA that lands the visitor
 * exactly where they need to be. Palette pulled from the AddVendTour
 * brand: teal, pink, purple, lime, orange.
 */
const ROLES = [
  {
    key: "organizer",
    Icon: Tent,
    color: "var(--avt-teal)",
    bg: "rgba(44,196,217,0.10)",
    ring: "rgba(44,196,217,0.30)",
    label: "Event Organizers",
    tagline: "Fill your market with the right vendors.",
    body: "Post an event, review applications with portfolios attached, and collect booth fees on the same page.",
    cta: "Post an event",
    href: "/register?role=organizer",
    testId: "role-organizer",
  },
  {
    key: "vendor",
    Icon: ShoppingBag,
    color: "var(--avt-pink)",
    bg: "rgba(236,72,153,0.10)",
    ring: "rgba(236,72,153,0.30)",
    label: "Vendors & Makers",
    tagline: "Book more markets, chase fewer spreadsheets.",
    body: "Build a warm profile. Filter events by city and category. Track every application from pending to approved.",
    cta: "Find your next booth",
    href: "/events",
    testId: "role-vendor",
  },
  {
    key: "nonprofit",
    Icon: Heart,
    color: "var(--avt-purple)",
    bg: "rgba(107,47,186,0.10)",
    ring: "rgba(107,47,186,0.30)",
    label: "Nonprofits",
    tagline: "Get verified. Get discovered. Get funded.",
    body: "Verify your 501(c)(3) in seconds. Unlock a 50% Maker Pro discount, a verified badge, and 0%-fee donations.",
    cta: "Verify your org",
    href: "/verify-nonprofit",
    testId: "role-nonprofit",
  },
  {
    key: "volunteer",
    Icon: HandHeart,
    color: "var(--avt-lime)",
    bg: "rgba(132,204,22,0.14)",
    ring: "rgba(132,204,22,0.40)",
    label: "Volunteers",
    tagline: "Show up. Help out. Make it happen.",
    body: "Sign up for shifts at fundraiser markets in your city. Pick your slot, sign the waiver, done.",
    cta: "Find events to help",
    href: "/events",
    testId: "role-volunteer",
  },
  {
    key: "donor",
    Icon: Gift,
    color: "var(--avt-orange)",
    bg: "rgba(249,115,22,0.10)",
    ring: "rgba(249,115,22,0.30)",
    label: "Donors",
    tagline: "Give directly. 100% goes to the nonprofit.",
    body: "Browse verified 501(c)(3)s on AddVendTour and donate in one tap. Tax receipt in your inbox instantly.",
    cta: "Explore Give Back",
    href: "/give-back",
    testId: "role-donor",
  },
];

export default function FiveUserTypes() {
  return (
    <section
      className="mx-auto max-w-7xl px-6 lg:px-10 pb-24"
      data-testid="five-user-types-section"
    >
      <div className="text-center max-w-3xl mx-auto mb-12">
        <div
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full"
          style={{
            backgroundColor: "rgba(107,47,186,0.10)",
            color: "var(--avt-purple)",
            border: "1.5px solid var(--avt-purple)",
          }}
        >
          <Heart className="h-3 w-3" fill="var(--avt-purple)" strokeWidth={0} />
          The Give Back Marketplace
        </div>
        <h2 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">
          <span style={{ color: "var(--avt-teal)" }}>Whoever</span>{" "}
          <span style={{ color: "var(--avt-purple)" }}>you are</span>,{" "}
          <span style={{ color: "var(--avt-orange)" }}>there&rsquo;s a spot</span>{" "}
          <span style={{ color: "var(--avt-pink)" }}>for you here.</span>
        </h2>
        <p
          className="mt-4 text-base leading-relaxed"
          style={{ color: "var(--ink-soft)" }}
        >
          AddVendTour brings five kinds of people together around one local event.
          Find yours &mdash; and jump straight in.
        </p>
      </div>

      <div
        className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5"
        data-testid="five-user-types-grid"
      >
        {ROLES.map(({ key, Icon, color, bg, ring, label, tagline, body, cta, href, testId }) => (
          <Link
            key={key}
            to={href}
            data-testid={testId}
            className="card-warm rounded-2xl p-6 flex flex-col transition-all hover:-translate-y-1"
            style={{
              boxShadow: `0 0 0 1px ${ring}`,
            }}
          >
            <div
              className="h-12 w-12 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: bg }}
            >
              <Icon className="h-6 w-6" style={{ color }} />
            </div>
            <div
              className="mt-4 text-[11px] uppercase tracking-widest"
              style={{ color }}
              data-testid={`${testId}-label`}
            >
              {label}
            </div>
            <div className="font-serif text-lg mt-1 leading-snug">{tagline}</div>
            <p
              className="mt-3 text-sm leading-relaxed flex-1"
              style={{ color: "var(--ink-soft)" }}
            >
              {body}
            </p>
            <div
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold"
              style={{ color }}
              data-testid={`${testId}-cta`}
            >
              {cta} <ArrowRight className="h-3.5 w-3.5" />
            </div>
          </Link>
        ))}
      </div>

      {/* Sub-footer stat strip */}
      <div
        className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t"
        style={{ borderColor: "var(--border-warm)" }}
        data-testid="five-user-types-stats"
      >
        {[
          { label: "Verified 501(c)(3)s", value: "0% platform fee", color: "var(--avt-purple)" },
          { label: "Local events", value: "42 open this week", color: "var(--avt-teal)" },
          { label: "Maker Pro trial", value: "7 days on us", color: "var(--avt-pink)" },
          { label: "Tax receipts", value: "Instant, in your inbox", color: "var(--avt-orange)" },
        ].map((s) => (
          <div key={s.label} className="text-center sm:text-left">
            <div className="font-serif text-xl" style={{ color: s.color }}>{s.value}</div>
            <div
              className="text-[11px] uppercase tracking-widest mt-1"
              style={{ color: "var(--ink-soft)" }}
            >
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
